// OpenStreetMap Nominatim Address Autocomplete
// Utilisation: ajouter la classe 'address-autocomplete' au champ de rue
// et configurer les data-attributes pour les champs liés

const AddressAutocomplete = {
  debounceDelay: 300,
  debounceTimers: {},

  init() {
    document.addEventListener('turbolinks:load', () => this.setupAll());
    document.addEventListener('DOMContentLoaded', () => this.setupAll());
  },

  setupAll() {
    // Recherche tous les préfixes d'adresse disponibles
    const prefixes = [
      'tenant_address',
      'guarantor_address',
      'property_address'
    ];

    prefixes.forEach(prefix => {
      this.initAddressAutocomplete(prefix);
    });
  },

  initAddressAutocomplete(prefix) {
    const streetInput = document.getElementById(prefix + '_street');
    const suggestionsContainer = document.getElementById(prefix + '_suggestions');
    const zipcodeInput = document.getElementById(prefix + '_zipcode');
    const cityInput = document.getElementById(prefix + '_city');
    const latitudeInput = document.getElementById(prefix + '_latitude');
    const longitudeInput = document.getElementById(prefix + '_longitude');

    if (!streetInput || !suggestionsContainer) return;

    // Éviter d'attacher les événements plusieurs fois
    if (streetInput.dataset.autocompleteInit === 'true') return;
    streetInput.dataset.autocompleteInit = 'true';

    streetInput.addEventListener('input', () => {
      const query = streetInput.value.trim();

      if (this.debounceTimers[prefix]) {
        clearTimeout(this.debounceTimers[prefix]);
      }

      if (query.length < 3) {
        suggestionsContainer.classList.add('hidden');
        suggestionsContainer.innerHTML = '';
        return;
      }

      this.debounceTimers[prefix] = setTimeout(() => {
        this.fetchAddresses(query, suggestionsContainer, streetInput, zipcodeInput, cityInput, latitudeInput, longitudeInput);
      }, this.debounceDelay);
    });

    // Fermer les suggestions quand on clique ailleurs
    document.addEventListener('click', (e) => {
      if (!streetInput.contains(e.target) && !suggestionsContainer.contains(e.target)) {
        suggestionsContainer.classList.add('hidden');
      }
    });

    // Navigation clavier
    streetInput.addEventListener('keydown', (e) => {
      const items = suggestionsContainer.querySelectorAll('.suggestion-item');
      const activeItem = suggestionsContainer.querySelector('.suggestion-item.bg-indigo-50');
      let activeIndex = Array.from(items).indexOf(activeItem);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (activeIndex < items.length - 1) {
          items.forEach(item => item.classList.remove('bg-indigo-50'));
          items[activeIndex + 1].classList.add('bg-indigo-50');
          items[activeIndex + 1].scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (activeIndex > 0) {
          items.forEach(item => item.classList.remove('bg-indigo-50'));
          items[activeIndex - 1].classList.add('bg-indigo-50');
          items[activeIndex - 1].scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeItem) {
          activeItem.click();
        }
      } else if (e.key === 'Escape') {
        suggestionsContainer.classList.add('hidden');
      }
    });
  },

  fetchAddresses(query, suggestionsContainer, streetInput, zipcodeInput, cityInput, latitudeInput, longitudeInput) {
    const url = 'https://nominatim.openstreetmap.org/search?' + new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: 1,
      limit: 5,
      countrycodes: 'fr'
    });

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'Hippobilier/1.0'
      }
    })
    .then(response => response.json())
    .then(data => {
      suggestionsContainer.innerHTML = '';

      if (data.length === 0) {
        suggestionsContainer.classList.add('hidden');
        return;
      }

      data.forEach((place, index) => {
        const item = document.createElement('div');
        item.className = 'suggestion-item px-4 py-2 cursor-pointer hover:bg-indigo-50 text-sm text-gray-700';
        if (index === 0) item.classList.add('bg-indigo-50');

        // Formater l'affichage simplifié : numéro + rue, code postal ville
        const address = place.address;
        let displayParts = [];

        // Rue
        let streetPart = [];
        if (address.house_number) streetPart.push(address.house_number);
        if (address.road) streetPart.push(address.road);
        if (streetPart.length > 0) {
          displayParts.push(streetPart.join(' '));
        } else {
          // Fallback si pas de rue (lieu-dit, etc.)
          displayParts.push(place.display_name.split(',')[0]);
        }

        // Code postal et ville
        const city = address.city || address.town || address.village || address.municipality || '';
        if (address.postcode && city) {
          displayParts.push(address.postcode + ' ' + city);
        } else if (city) {
          displayParts.push(city);
        }

        item.textContent = displayParts.join(', ');

        item.addEventListener('click', () => {

          // Construire l'adresse de rue
          let streetParts = [];
          if (address.house_number) streetParts.push(address.house_number);
          if (address.road) streetParts.push(address.road);

          streetInput.value = streetParts.join(' ') || place.display_name.split(',')[0];

          // Code postal
          if (address.postcode && zipcodeInput) {
            zipcodeInput.value = address.postcode;
          }

          // Ville
          if (cityInput) {
            cityInput.value = address.city || address.town || address.village || address.municipality || '';
          }

          // Coordonnées
          if (latitudeInput) {
            latitudeInput.value = place.lat;
          }
          if (longitudeInput) {
            longitudeInput.value = place.lon;
          }

          suggestionsContainer.classList.add('hidden');
        });

        suggestionsContainer.appendChild(item);
      });

      suggestionsContainer.classList.remove('hidden');
    })
    .catch(error => {
      console.error('Erreur lors de la recherche d\'adresses:', error);
      suggestionsContainer.classList.add('hidden');
    });
  }
};

export default AddressAutocomplete;
